/**
 * OwnerAcceptanceLinkBar - Envio do aceite ao cliente/proprietário por link exclusivo
 *
 * Exibido abaixo do participante "Cliente / proprietário" pendente no modal da vistoria.
 * O link é separado do link de execução: o cliente confirma o CPF/CNPJ do proprietário
 * congelado na vistoria, vê o resumo somente leitura e só pode dar o aceite.
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-05 v1.0.0 - Criação inicial.
 */
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Ban, Copy, Link2, Loader2, MessageCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { StatusDotLabel } from '@/components/ui/status-dot-label';
import { buildPublicChecklistUrl, useChecklistAcceptanceLink } from '@/hooks/useChecklistPublicLink';

export function OwnerAcceptanceLinkBar({ instanceId }: { instanceId: string }) {
  const { data, isLoading, generate, revoke } = useChecklistAcceptanceLink(instanceId);
  if (isLoading || !data) return null;

  const link = data.link && !data.link.is_expired ? data.link : null;
  const url = link ? buildPublicChecklistUrl(link.token) : '';
  const busy = generate.isPending || revoke.isPending;

  /** Copia o link para a área de transferência. */
  const copy = async () => {
    await navigator.clipboard.writeText(url);
    toast.success('Link copiado');
  };

  /** Abre o WhatsApp com a mensagem pronta (o cliente escolhe o contato). */
  const whatsapp = () => {
    const text = encodeURIComponent(`Olá! Confira a vistoria do seu veículo e registre seu aceite: ${url}`);
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md bg-muted/40 px-2 py-1.5 text-xs">
      <Link2 className="h-3.5 w-3.5 text-primary" />
      {!data.has_owner_document ? (
        <span className="text-muted-foreground">Link ao cliente indisponível: {data.unavailable_reason}</span>
      ) : link ? (
        <>
          <StatusDotLabel tone={link.opened ? 'info' : 'warning'} label={link.opened ? 'Link aberto pelo cliente' : 'Link enviado'} />
          <span className="text-muted-foreground">até {format(new Date(link.expires_at), 'dd/MM/yyyy', { locale: ptBR })}</span>
          <div className="ml-auto flex gap-1">
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={copy} title="Copiar link"><Copy className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={whatsapp} title="Enviar por WhatsApp"><MessageCircle className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant="ghost" className="h-7 px-2" disabled={busy} onClick={() => generate.mutate()} title="Gerar novo link"><RefreshCw className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-destructive" disabled={busy} onClick={() => revoke.mutate()} title="Revogar link"><Ban className="h-3.5 w-3.5" /></Button>
          </div>
        </>
      ) : (
        <>
          <span className="text-muted-foreground">Cliente não está presente?</span>
          <Button size="sm" variant="outline" className="ml-auto h-7 text-xs" disabled={busy || !data.owner_pending}
            onClick={() => generate.mutate()}>
            {generate.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}Enviar link ao cliente
          </Button>
        </>
      )}
    </div>
  );
}
